import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './index.css';
import Footer from '../About/Footer';

const CapitalOffice = () => {
    const [openFaq, setOpenFaq] = useState(0);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        {
            q: 'Who is Capital Office for?',
            a: 'It is designed for founders actively preparing or raising a focused round, and for investors working from a defined, active mandate.'
        },
        {
            q: 'How are investor matches selected?',
            a: 'We evaluate stage alignment, sector focus, check size, thesis fit, and active deployment windows to ensure introductions make sense for both parties.'
        },
        {
            q: 'Does Markoknow guarantee funding?',
            a: 'No platform can guarantee capital. We prepare your narrative, structure your diligence data, and place you in qualified rooms to maximize conversion.'
        },
        {
            q: 'What should I prepare before applying?',
            a: 'Bring your initial pitch deck, historical traction numbers, targeted raise amount, and clarity on your primary growth engine.'
        },
        {
            q: 'How are private rooms protected?',
            a: 'Deal rooms operate with role-based access, watermarked documents, NDA enforcement capabilities, and granular viewer analytics.'
        }
    ];

    return (
        <div className="co-page">
            {/* =========================================================================
          SECTION 1: HERO SECTION
          ========================================================================= */}
            <section className="co-hero-section">
                <div className="co-hero-left">
                    <div className="co-tag">
                        <span className="co-dot"></span>
                        CAPITAL OFFICE BY MARKOKNOW
                    </div>
                    <h1 className="co-hero-title">
                        Capital,<br />
                        <span className="co-hero-italic">with context.</span>
                    </h1>
                    <p className="co-hero-desc">
                        Meet relevant investors through curated mandates, private rooms and round preparation—built for founders who want better conversations, not a longer contact list.
                    </p>
                    <div className="co-hero-actions">
                        <Link to="/capitalofficeform" style={{ textDecoration: 'none' }}>
                            <button className="co-btn-primary">
                                Prepare your round <span className="co-btn-arrow">↗</span>
                            </button>
                        </Link>
                        <a href="#how-it-works" className="co-link-action">
                            See how Capital Office works ↓
                        </a>
                    </div>

                    <div className="co-hero-features-row">
                        <div className="co-feat-item">
                            <span className="co-feat-num">01</span>
                            <span className="co-feat-text">Mandate-led<br />matching</span>
                        </div>
                        <div className="co-feat-sep"></div>
                        <div className="co-feat-item">
                            <span className="co-feat-num">02</span>
                            <span className="co-feat-text">Private<br />deal rooms</span>
                        </div>
                        <div className="co-feat-sep"></div>
                        <div className="co-feat-item">
                            <span className="co-feat-num">03</span>
                            <span className="co-feat-text">Prepared<br />founders</span>
                        </div>
                    </div>
                </div>

                <div className="co-hero-right">
                    <div className="co-floating-tag tag-curated">CURATED ACCESS</div>

                    <div className="co-radar-wrapper">
                        <div className="co-radar-ring ring-lg"></div>
                        <div className="co-radar-ring ring-md"></div>

                        {/* Dark Deal Room Card */}
                        <div className="co-dark-card">
                            <div className="co-dark-card-head">
                                <span className="co-dark-meta">PRIVATE ROOM</span>
                                <span className="co-dark-status">OPEN</span>
                            </div>
                            <h3 className="co-dark-card-title">
                                Series Seed<br />Investor Room
                            </h3>

                            <div className="co-dark-stats-grid">
                                <div className="co-dark-stat-box">
                                    <div className="co-dark-stat-val">04</div>
                                    <div className="co-dark-stat-lbl">MATCHED<br />MANDATES</div>
                                </div>
                                <div className="co-dark-stat-box">
                                    <div className="co-dark-stat-val">08/10</div>
                                    <div className="co-dark-stat-lbl">READINESS<br />SCORE</div>
                                </div>
                            </div>

                            <div className="co-dark-checklist">
                                <div className="co-dark-check-row">
                                    <span>Deck & narrative</span>
                                    <span className="co-status-ready">READY</span>
                                </div>
                                <div className="co-dark-check-row">
                                    <span>Round data room</span>
                                    <span className="co-status-ready">READY</span>
                                </div>
                                <div className="co-dark-check-row">
                                    <span>Meeting rehearsal</span>
                                    <span className="co-status-next">NEXT</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="co-floating-tag tag-ready">ROUND READY</div>
                </div>
            </section>

            {/* Marquee Navigation Bar */}
            <div className="co-marquee-bar">
                <div className="co-marquee-item">INVESTOR MANDATES</div>
                <span className="co-star">✦</span>
                <div className="co-marquee-item">PRIVATE ROOMS</div>
                <span className="co-star">✦</span>
                <div className="co-marquee-item">ROUND NARRATIVE</div>
                <span className="co-star">✦</span>
                <div className="co-marquee-item">DILIGENCE READINESS</div>
            </div>

            {/* =========================================================================
          SECTION 2: HOW IT WORKS (Timeline Flow)
          ========================================================================= */}
            <section className="co-section co-how-it-works" id="how-it-works">
                <div className="co-sec-header">
                    <div className="co-tag">HOW IT WORKS</div>
                    <h2 className="co-sec-title">From round ambition to investor-ready.</h2>
                    <p className="co-sec-desc">
                        A focused four-step process designed to protect founder time and create higher-quality investor conversations.
                    </p>
                </div>

                <div className="co-timeline-container">
                    <div className="co-timeline-line"></div>

                    <div className="co-timeline-grid">
                        {/* Step 1 */}
                        <div className="co-timeline-card-wrapper">
                            <div className="co-step-badge">01</div>
                            <div className="co-step-card">
                                <div className="co-step-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5c4eb4" strokeWidth="1.7">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
                                    </svg>
                                </div>
                                <div className="co-step-index">01</div>
                                <h4 className="co-step-heading">Define the mandate</h4>
                                <p className="co-step-text">
                                    Align on stage, sector, geography, cheque size and the outcome you need.
                                </p>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="co-timeline-card-wrapper">
                            <div className="co-step-badge">02</div>
                            <div className="co-step-card">
                                <div className="co-step-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5c4eb4" strokeWidth="1.7">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        <path d="m9 15 2 2 4-4"></path>
                                    </svg>
                                </div>
                                <div className="co-step-index">02</div>
                                <h4 className="co-step-heading">Prepare the round</h4>
                                <p className="co-step-text">
                                    Strengthen your narrative, deck, data room and investor-facing materials.
                                </p>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="co-timeline-card-wrapper">
                            <div className="co-step-badge">03</div>
                            <div className="co-step-card">
                                <div className="co-step-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5c4eb4" strokeWidth="1.7">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="9" cy="7" r="4"></circle>
                                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                    </svg>
                                </div>
                                <div className="co-step-index">03</div>
                                <h4 className="co-step-heading">Curate the match</h4>
                                <p className="co-step-text">
                                    Surface investors whose active thesis and mandate fit your company.
                                </p>
                            </div>
                        </div>

                        {/* Step 4 */}
                        <div className="co-timeline-card-wrapper">
                            <div className="co-step-badge">04</div>
                            <div className="co-step-card">
                                <div className="co-step-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5c4eb4" strokeWidth="1.7">
                                        <path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14"></path>
                                        <path d="M2 20h20"></path>
                                        <path d="M14 12v.01"></path>
                                    </svg>
                                </div>
                                <div className="co-step-index">04</div>
                                <h4 className="co-step-heading">Enter the room</h4>
                                <p className="co-step-text">
                                    Meet privately, manage follow-ups and move qualified interest toward diligence.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="co-callout-banner">
                    <div className="co-callout-left">
                        <span className="co-star-purple">✦</span>
                        <span>One clear process. Fewer cold introductions. Better-fit conversations.</span>
                    </div>
                    <a href="#overview" className="co-callout-link">
                        See what founders receive →
                    </a>
                </div>
            </section>

            {/* =========================================================================
          SECTION 3: WHY CAPITAL OFFICE (3 Cards + Dark Footer Bar)
          ========================================================================= */}
            <section className="co-section co-why-section">
                <div className="co-sec-header">
                    <div className="co-tag">WHY CAPITAL OFFICE</div>
                    <h2 className="co-sec-title">
                        Fundraising is not a contact list.<br />
                        It is a prepared conversation.
                    </h2>
                    <p className="co-sec-desc">
                        We turn your round into a clear mandate, match it with relevant capital, and create focused spaces where serious conversations can move forward.
                    </p>
                </div>

                <div className="co-three-cards-grid">
                    {/* Card 1 */}
                    <div className="co-feature-card">
                        <div className="co-fc-top">
                            <span className="co-fc-num">01</span>
                            <div className="co-fc-icon">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0b1329" strokeWidth="1.7">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <circle cx="12" cy="12" r="6"></circle>
                                    <circle cx="12" cy="12" r="2"></circle>
                                </svg>
                            </div>
                        </div>
                        <h4 className="co-fc-title">Curated mandates</h4>
                        <p className="co-fc-desc">
                            Share a precise investment mandate and meet investors aligned with your stage, sector and cheque size.
                        </p>
                        <span className="co-fc-arrow">→</span>
                    </div>

                    {/* Card 2 */}
                    <div className="co-feature-card">
                        <div className="co-fc-top">
                            <span className="co-fc-num">02</span>
                            <div className="co-fc-icon">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0b1329" strokeWidth="1.7">
                                    <path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14"></path>
                                    <path d="M2 20h20"></path>
                                </svg>
                            </div>
                        </div>
                        <h4 className="co-fc-title">Private rooms</h4>
                        <p className="co-fc-desc">
                            Move from introductions to confidential diligence in a dedicated, structured deal room.
                        </p>
                        <span className="co-fc-arrow">→</span>
                    </div>

                    {/* Card 3 */}
                    <div className="co-feature-card">
                        <div className="co-fc-top">
                            <span className="co-fc-num">03</span>
                            <div className="co-fc-icon">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0b1329" strokeWidth="1.7">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <path d="m9 15 2 2 4-4"></path>
                                </svg>
                            </div>
                        </div>
                        <h4 className="co-fc-title">Round preparation</h4>
                        <p className="co-fc-desc">
                            Sharpen your narrative, materials and data before the first investor conversation.
                        </p>
                        <span className="co-fc-arrow">→</span>
                    </div>
                </div>

                {/* Dark Footer Strip */}
                <div className="co-why-strip">
                    <div className="co-strip-col">
                        <div className="co-strip-icon-circle">✓</div>
                        <span>Curated, not crowded</span>
                    </div>
                    <div className="co-strip-sep"></div>
                    <div className="co-strip-col">
                        <div className="co-strip-icon-circle">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            </svg>
                        </div>
                        <span>Founder-ready materials</span>
                    </div>
                    <div className="co-strip-sep"></div>
                    <div className="co-strip-col">
                        <div className="co-strip-icon-circle">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                        </div>
                        <span>Private by design</span>
                    </div>
                </div>
            </section>

            {/* =========================================================================
          SECTION 4: PRIVATE INVESTOR ROOMS (Interactive Dashboard Graphic)
          ========================================================================= */}
            <section className="co-dark-section" id="overview">
                <div className="co-dark-split">
                    <div className="co-dark-text-side">
                        <div className="co-tag text-purple-light">PRIVATE INVESTOR ROOMS</div>
                        <h2 className="co-dark-h2">
                            One room for<br />every serious<br />conversation.
                        </h2>
                        <p className="co-dark-p">
                            Bring the founder, the right investors and the essential round materials into a focused, confidential workspace.
                        </p>

                        <ul className="co-dark-checks">
                            <li>
                                <div className="co-chk-circle">✓</div>
                                Role-based access
                            </li>
                            <li>
                                <div className="co-chk-circle">✓</div>
                                Structured diligence
                            </li>
                            <li>
                                <div className="co-chk-circle">✓</div>
                                Clear next actions
                            </li>
                        </ul>

                        <button className="co-btn-purple">
                            Explore private rooms →
                        </button>
                    </div>

                    {/* Deal Room App Dashboard Window */}
                    <div className="co-dark-window-side">
                        <div className="co-mock-app">
                            <div className="co-app-header">
                                <div className="co-app-room-name">
                                    <span className="co-app-door-icon">🚪</span>
                                    <strong>Series A · Investor Room</strong>
                                </div>
                                <div className="co-app-members">
                                    <span className="co-app-private-pill">● Private</span>
                                    <div className="co-avatar">AM</div>
                                    <div className="co-avatar">JL</div>
                                    <div className="co-avatar">SK</div>
                                </div>
                            </div>

                            <div className="co-app-body">
                                {/* App Left Nav */}
                                <div className="co-app-nav">
                                    <div className="co-nav-item active">⊞ Overview</div>
                                    <div className="co-nav-item">📁 Materials</div>
                                    <div className="co-nav-item">💬 Questions</div>
                                    <div className="co-nav-item">📈 Activity</div>
                                </div>

                                {/* App Content Center */}
                                <div className="co-app-content">
                                    <div className="co-app-readiness">
                                        <div className="co-app-bar-head">
                                            <strong>Round readiness</strong>
                                            <span className="co-app-perc">82% prepared</span>
                                        </div>
                                        <div className="co-progress-track">
                                            <div className="co-progress-fill" style={{ width: '82%' }}></div>
                                        </div>
                                    </div>

                                    <div className="co-app-files">
                                        <div className="co-file-row">
                                            <div className="co-file-title">
                                                <span>📄</span> Pitch deck
                                            </div>
                                            <span className="co-badge-green">✓ Ready</span>
                                        </div>
                                        <div className="co-file-row">
                                            <div className="co-file-title">
                                                <span>📊</span> Financial model
                                            </div>
                                            <span className="co-badge-green">✓ Ready</span>
                                        </div>
                                        <div className="co-file-row">
                                            <div className="co-file-title">
                                                <span>🔒</span> Data room
                                            </div>
                                            <span className="co-badge-purple">4 updates</span>
                                        </div>
                                    </div>

                                    <div className="co-app-activity-toast">
                                        👥 2 investors reviewed materials today
                                    </div>
                                </div>

                                {/* App Next Conversation Card */}
                                <div className="co-app-side-card">
                                    <h4>Next conversation</h4>
                                    <div className="co-cal-icon-box">📅</div>
                                    <h5>Thursday · 4:30 PM</h5>
                                    <p>Northstar Ventures</p>
                                    <button className="co-btn-outline-purple">View agenda</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="co-dark-bottom-bar">
                    <span>Confidential by default</span>
                    <span className="co-dot-sep">●</span>
                    <span>Founder-controlled access</span>
                    <span className="co-dot-sep">●</span>
                    <span>Built for focused diligence</span>
                </div>
            </section>

            {/* =========================================================================
          SECTION 5: ROUND PREPARATION (Interactive Cards Graphic)
          ========================================================================= */}
            <section className="co-section co-prep-section">
                <div className="co-prep-split">
                    <div className="co-prep-left">
                        <div className="co-tag">ROUND PREPARATION</div>
                        <h2 className="co-sec-title">
                            Walk into the room<br />
                            with a sharper story.
                        </h2>
                        <p className="co-sec-desc">
                            We help you turn scattered fundraising materials into one coherent investor narrative—clear, credible and ready for scrutiny.
                        </p>

                        <button className="co-btn-outline-dark">
                            See the preparation process →
                        </button>

                        <div className="co-quote-block">
                            Designed to reduce avoidable questions before the first meeting.
                        </div>
                    </div>

                    {/* Floating Prepared Artifact Cards */}
                    <div className="co-prep-right">
                        <div className="co-card-stack">
                            {/* Main Top Card: Investor Narrative */}
                            <div className="co-stack-card card-narrative">
                                <div className="co-card-badge-top">
                                    <span>💼 Founder Round Kit</span>
                                </div>
                                <div className="co-sn-head">
                                    <strong>Investor narrative</strong>
                                    <span className="co-badge-green-sm">✓ Ready</span>
                                </div>
                                <p className="co-sn-sub">Positioning · Why now · Use of funds</p>
                                <div className="co-chart-mock">
                                    <svg width="100%" height="50" viewBox="0 0 200 50">
                                        <path d="M10 40 Q 60 10, 110 35 T 190 10" fill="none" stroke="#5c4eb4" strokeWidth="2.5" />
                                        <circle cx="10" cy="40" r="4" fill="#5c4eb4" />
                                        <circle cx="60" cy="20" r="4" fill="#5c4eb4" />
                                        <circle cx="110" cy="35" r="4" fill="#5c4eb4" />
                                        <circle cx="190" cy="10" r="4" fill="#5c4eb4" />
                                    </svg>
                                </div>
                            </div>

                            {/* Pitch Deck Checklist Card */}
                            <div className="co-stack-card card-deck-review">
                                <h5>Pitch deck review</h5>
                                <ul>
                                    <li><span className="co-check-g">✓</span> Problem and insight</li>
                                    <li><span className="co-check-g">✓</span> Market and wedge</li>
                                    <li><span className="co-check-g">✓</span> Round and milestones</li>
                                </ul>
                            </div>

                            {/* Financial Model Mini Card */}
                            <div className="co-stack-card card-fin-model">
                                <h5>Financial model</h5>
                                <div className="co-chart-bar-mock">
                                    <svg width="100%" height="40" viewBox="0 0 160 40">
                                        <path d="M5 35 L 50 25 L 90 30 L 150 5" fill="none" stroke="#5c4eb4" strokeWidth="2" />
                                    </svg>
                                </div>
                                <span className="co-status-pill-sub">✓ Assumptions aligned</span>
                            </div>

                            {/* Data Room Checklist Mini Card */}
                            <div className="co-stack-card card-dataroom">
                                <h5>Data room checklist</h5>
                                <p>18 / 20 complete</p>
                                <div className="co-progress-track">
                                    <div className="co-progress-fill" style={{ width: '90%' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4 Bottom Value Props */}
                <div className="co-prep-four-cols">
                    <div className="co-p4-item">
                        <div className="co-p4-icon">🎯</div>
                        <h5>Clear positioning</h5>
                        <p>A concise reason to believe.</p>
                    </div>
                    <div className="co-p4-item">
                        <div className="co-p4-icon">📊</div>
                        <h5>Defensible numbers</h5>
                        <p>Assumptions investors can follow.</p>
                    </div>
                    <div className="co-p4-item">
                        <div className="co-p4-icon">📑</div>
                        <h5>Complete materials</h5>
                        <p>The essentials ready to share.</p>
                    </div>
                    <div className="co-p4-item">
                        <div className="co-p4-icon">👤</div>
                        <h5>Confident delivery</h5>
                        <p>A narrative you can own.</p>
                    </div>
                </div>
            </section>

            {/* =========================================================================
          SECTION 6: CURATED MANDATES (Match Center Graphic)
          ========================================================================= */}
            <section className="co-section co-mandates-section">
                <div className="co-sec-header text-center">
                    <div className="co-tag centered-tag">CURATED MANDATES</div>
                    <h2 className="co-sec-title">Relevant capital starts with a precise fit.</h2>
                    <p className="co-sec-desc">
                        We compare the reality of your round with active investor criteria—then create introductions where both sides have a reason to engage.
                    </p>
                </div>

                {/* Match Bridge Diagram */}
                <div className="co-matching-bridge-container">
                    {/* Round Box (Left) */}
                    <div className="co-bridge-box">
                        <div className="co-bb-head">
                            <div className="co-bb-icon">👤</div>
                            <h4>Your round</h4>
                        </div>
                        <div className="co-bb-list">
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">🌱 Stage</span>
                                <span className="co-bb-val">Seed</span>
                            </div>
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">⊞ Sector</span>
                                <span className="co-bb-val">B2B SaaS</span>
                            </div>
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">₹ Target</span>
                                <span className="co-bb-val">₹8 Cr</span>
                            </div>
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">🌐 Geography</span>
                                <span className="co-bb-val">India</span>
                            </div>
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">📊 Use of funds</span>
                                <span className="co-bb-val">Go-to-market</span>
                            </div>
                        </div>
                    </div>

                    {/* Center Connection Circle */}
                    <div className="co-bridge-center">
                        <div className="co-connector-lines left-lines">
                            <span></span><span></span><span></span>
                        </div>

                        <div className="co-score-circle">
                            <span className="co-score-title">Mandate fit</span>
                            <span className="co-score-number">92%</span>
                        </div>

                        <div className="co-connector-lines right-lines">
                            <span></span><span></span><span></span>
                        </div>

                        <div className="co-align-pill">
                            ✓ High alignment
                        </div>
                    </div>

                    {/* Investor Mandate Box (Right) */}
                    <div className="co-bridge-box">
                        <div className="co-bb-head">
                            <div className="co-bb-icon">🏛️</div>
                            <h4>Active mandate</h4>
                        </div>
                        <div className="co-bb-list">
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">🌱 Stage</span>
                                <span className="co-bb-val">Seed to Series A</span>
                            </div>
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">⊞ Sector</span>
                                <span className="co-bb-val">Enterprise software</span>
                            </div>
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">₹ Cheque</span>
                                <span className="co-bb-val">₹4–10 Cr</span>
                            </div>
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">🌐 Geography</span>
                                <span className="co-bb-val">India & SEA</span>
                            </div>
                            <div className="co-bb-row">
                                <span className="co-bb-lbl">🎯 Thesis</span>
                                <span className="co-bb-val">Efficient growth</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Two Audience Callouts */}
                <div className="co-audience-grid">
                    <div className="co-aud-card">
                        <span className="co-aud-tag">FOR FOUNDERS</span>
                        <h4>Building a focused institutional round</h4>
                        <p>
                            Best suited to founders with a clear raise, prepared materials and the capacity to engage seriously with aligned investors.
                        </p>
                        <div className="co-aud-pills">
                            <span>🚀 Pre-seed to Series B</span>
                            <span>⚡ Tech-enabled</span>
                            <span>📈 Actively raising</span>
                        </div>
                    </div>

                    <div className="co-aud-card">
                        <span className="co-aud-tag">FOR INVESTORS</span>
                        <h4>Looking for mandate-aligned opportunities</h4>
                        <p>
                            Designed for funds, family offices and angels with an active thesis, defined cheque range and a genuine appetite to evaluate.
                        </p>
                        <div className="co-aud-pills">
                            <span>👥 Funds</span>
                            <span>🏛️ Family offices</span>
                            <span>👤 Operator angels</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* =========================================================================
          SECTION 7: FAQ & CALL TO ACTION BANNER
          ========================================================================= */}
            <section className="co-section co-faq-cta-section">
                <div className="co-faq-split">
                    <div className="co-faq-left">
                        <div className="co-tag">FREQUENTLY ASKED QUESTIONS</div>
                        <h2 className="co-sec-title">
                            A few things founders<br />
                            ask before entering<br />
                            the room.
                        </h2>
                        <p className="co-sec-desc">
                            Capital Office is selective by design. Here is what to expect before you submit your round.
                        </p>
                    </div>

                    <div className="co-faq-right">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className={`co-faq-card ${openFaq === index ? 'active' : ''}`}
                                onClick={() => toggleFaq(index)}
                            >
                                <div className="co-faq-card-header">
                                    <span className="co-faq-q">{faq.q}</span>
                                    <span className="co-faq-toggle">
                                        {openFaq === index ? '−' : '+'}
                                    </span>
                                </div>
                                {openFaq === index && (
                                    <p className="co-faq-a">{faq.a}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom Blue/Purple CTA Banner */}
                <div className="co-bottom-cta-banner">
                    {/* Constellation Network Overlay Lines */}
                    <div className="co-constellation-bg"></div>

                    <div className="co-cta-banner-left">
                        <span className="co-cta-banner-tag">READY WHEN YOUR ROUND IS</span>
                        <h3 className="co-cta-banner-h3">
                            Bring a clearer round into the right room.
                        </h3>
                        <p className="co-cta-banner-p">
                            Tell us what you are building, how much you are raising and which investor mandate fits the next chapter.
                        </p>
                        <div className="co-cta-btns">
                            <button className="co-btn-white">Apply to Capital Office</button>
                            <button className="co-btn-trans">Share an investor mandate</button>
                        </div>
                    </div>

                    <div className="co-cta-banner-right">
                        <div className="co-card-checklist-box">
                            <h4>Before you apply</h4>
                            <ul>
                                <li><span className="co-chk-blue">✓</span> A defined raise</li>
                                <li><span className="co-chk-blue">✓</span> Current materials</li>
                                <li><span className="co-chk-blue">✓</span> Founder availability</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default CapitalOffice;